import React, { useState, useEffect } from 'react';


interface AuthImageProps {
  url: string;
  alt?: string;
  style?: React.CSSProperties;
  className?: string;
  width?: string | number;
  height?: string | number;
}

const PLACEHOLDER = '/defaultplaceholder.png';

// Renderiza siempre un <img> (nunca un <div>Cargando…</div> que provocaba
// reflow y solapamientos en PanelSus). Empieza en el placeholder y lo
// sustituye cuando la imagen real (autenticada o pública) está disponible.
// Si la petición autenticada falla por cualquier motivo, mantiene el
// placeholder para que el layout no se rompa.
const AuthImage: React.FC<AuthImageProps> = ({ url, alt = '', style, className, width, height }) => {
  const [imgSrc, setImgSrc] = useState<string>(PLACEHOLDER);

  useEffect(() => {
    let objectUrl: string | null = null;
    let isMounted = true;

    if (!url) {
      // URL vacía o nula (p.ej. getUrlImage(undefined)): nada que pedir,
      // dejamos el placeholder.
      setImgSrc(PLACEHOLDER);
      return () => { isMounted = false; };
    }

    const fetchImage = async () => {
      try {
        // La app guarda el JWT bajo la clave 'tokensus' (ver
        // _base.service.js:28). Antes leíamos 'token' y la cabecera salía
        // como "Bearer null", lo que provocaba 401 perenne en /api/image/*.
        const token = localStorage.getItem('tokensus');
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!isMounted) return;
        if (response.ok) {
          const blob = await response.blob();
          if (!isMounted) return;
          objectUrl = URL.createObjectURL(blob);
          setImgSrc(objectUrl);
        } else {
          setImgSrc(PLACEHOLDER);
        }
      } catch (error) {
        console.error("Error cargando la imagen segura", error);
        if (isMounted) setImgSrc(PLACEHOLDER);
      }
    };

    if (url.includes('/api/image/') || url.includes('/api/file/')) {
      fetchImage();
    } else {
      setImgSrc(url);
    }

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      style={style}
      className={className}
      width={width}
      height={height}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        if (target.src.indexOf(PLACEHOLDER) === -1) {
          target.src = PLACEHOLDER;
        }
      }}
    />
  );
};

export default AuthImage;
